import { useTranslation } from "react-i18next";
import { RotateCcw, CreditCard } from "lucide-react";
import { ConfirmDialog } from "@tellescope/ui";
import { formatDate, formatCurrency, formatCardBrand } from "@tellescope/utils";
import { getStripePlanProductName } from "../../../hooks/useStripeSubscriptions";
import type { StripeSubscription } from "../../../hooks/useStripeSubscriptions";
import { useStripeSubscriptionMutation } from "../../../hooks/useStripeSubscriptionMutations";

interface UndoCancelConfirmDialogProps {
  subscription: StripeSubscription;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function UndoCancelConfirmDialog({ subscription, open, onOpenChange, onSuccess }: UndoCancelConfirmDialogProps) {
  const { t } = useTranslation("payments");
  const undoCancelMutation = useStripeSubscriptionMutation();

  const { effectivePlan } = subscription;
  const productName = getStripePlanProductName(effectivePlan);
  const cancelDate = formatDate(subscription.current_period_end * 1000);
  const price = effectivePlan ? formatCurrency(effectivePlan.amount, effectivePlan.currency) : null;
  const interval = effectivePlan?.interval;
  const cardInfo = subscription.default_payment_method?.card;

  const handleConfirm = () => {
    undoCancelMutation.mutate(
      {
        integration: "Stripe",
        type: "subscription-undo-cancel",
        id: subscription.id,
      },
      { onSuccess }
    );
  };

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      variant="default"
      icon={<RotateCcw className="h-5 w-5" />}
      title={t("Keep Your Subscription")}
      description={
        productName
          ? t(
              "Your subscription to {{productName}} is scheduled to cancel on {{date}}. Would you like to continue your subscription?",
              { productName, date: cancelDate }
            )
          : t("Your subscription is scheduled to cancel on {{date}}. Would you like to keep it active?", {
              date: cancelDate,
            })
      }
      confirmText={t("Keep My Subscription")}
      cancelText={t("Let It Cancel")}
      onConfirm={handleConfirm}
      loading={undoCancelMutation.isPending}
      customContent={
        <div className="mt-2 p-3 bg-muted rounded-md text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t("Next billing")}</span>
            <span className="font-medium">{cancelDate}</span>
          </div>
          {price !== null && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("Amount")}</span>
              <span className="font-medium">
                {price}/{interval}
              </span>
            </div>
          )}
          {cardInfo && (
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{t("Payment method")}</span>
              <span className="flex items-center gap-1">
                <CreditCard className="h-3 w-3" />
                {formatCardBrand(cardInfo.brand)} ****{cardInfo.last4}
              </span>
            </div>
          )}
        </div>
      }
    />
  );
}
